import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Paperclip, ArrowRight } from "lucide-react";

/**
 * ChatPreview — interactive AI demo widget on the public Landing page.
 *
 * Static-looking widget with 3 baked-in preview messages. Once the user hits
 * a suggestion chip OR types + sends, the widget:
 *   1. appends a user bubble (saffron, right-aligned)
 *   2. shows a 3-dot typing indicator for ~1.5s
 *   3. picks a canned response by keyword-matching the message
 *   4. word-streams that response into a fresh agent bubble
 *   5. reveals the "See your real intelligence →" CTA below the thread
 *
 * NO real API is called. Everything is client-side and deterministic so the
 * demo is fast, reliable, and works without auth.
 *
 * Imperative API (via ref):
 *   chatPreviewRef.current?.ask("Is my family eating healthy?")
 *
 * The Landing page uses this to wire the suggestion chips (which live outside
 * the widget for layout reasons) into the same conversation thread.
 */

const INITIAL_MESSAGES = [
  {
    id: "seed-1",
    role: "agent",
    kind: "opener",
    text:
      "Hi! Drop your Blinkit, Zepto or Zomato invoice — or just ask me anything about your food spending and health.",
  },
  {
    id: "seed-2",
    role: "user",
    text: "Is my family eating healthy?",
  },
  {
    id: "seed-3",
    role: "agent",
    kind: "insight",
    text:
      "Your fresh produce buying dropped 40% over 3 months while processed food rose 28%. Your nutrition score is 54 — I can help bring it back to 70 with small changes to your weekly grocery list.",
  },
];

// Keyword → canned response.
// Order matters — first match wins. Kept explicit for auditability.
const RESPONSE_LIBRARY = [
  {
    id: "upload",
    match: /(upload|invoice|blinkit|zepto|swiggy|zomato|screenshot|pdf|drop|attach)/i,
    text:
      "Just drag your PDF invoice or screenshot into the upload zone. I will extract every item, price and category and give you your complete food intelligence report in under 60 seconds. No signup needed for your first 5 invoices.",
  },
  {
    id: "health",
    match: /(health|nutrition|eat|healthy|score|junk|fresh|produce|diet|family)/i,
    text:
      "Your household nutrition score is 54 — down from 68 over 3 months. Fresh produce buying dropped 40% while convenience food rose 28%. Adding spinach, tomatoes and lentils to your weekly Blinkit order brings your score back to 62 within a month.",
  },
  {
    id: "spend",
    match: /(spend|money|cost|budget|much|pay|expensive|₹|rupee|amount|total)/i,
    text:
      "Urban Indian families average ₹18,400 per month across all food apps — but most have no idea of their true number. Upload your last 3 invoices and I will show you your exact figure in 60 seconds.",
  },
  {
    id: "pattern",
    match: /(pattern|order|habit|when|friday|wednesday|craving|frequency|timing)/i,
    text:
      "Most people order comfort food on Friday evenings after a long week without realising it. Wednesday sees 3x more quick commerce orders than other days. Your patterns are completely unique — upload an invoice and I will decode yours specifically.",
  },
];

const FALLBACK_RESPONSE =
  "Great question. Upload your invoices or screenshots from any food app and I will answer that specifically for your household — not generic averages. Your data, your intelligence.";

function classifyResponse(text) {
  for (const rule of RESPONSE_LIBRARY) {
    if (rule.match.test(text)) return { id: rule.id, text: rule.text };
  }
  return { id: "fallback", text: FALLBACK_RESPONSE };
}

const TYPING_DELAY_MS = 1500;
const STREAM_WORD_INTERVAL_MS = 45;

const ChatPreview = forwardRef(function ChatPreview(_props, ref) {
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [replyDelivered, setReplyDelivered] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimer = useRef(null);
  const streamTimer = useRef(null);

  // Best-effort cleanup: cancel any pending timers on unmount so React 18
  // StrictMode's simulated remount doesn't leak an in-flight demo stream.
  // Note: we do NOT gate state updates behind an isMounted ref — StrictMode
  // creates a fresh ref on the real second mount but the timer callbacks
  // captured earlier retain the OLD ref, and would see `false` forever,
  // silently killing the demo. React 18 safely no-ops setState on unmounted
  // components, so the guard is neither needed nor safe here.
  useEffect(() => {
    return () => {
      if (typingTimer.current) clearTimeout(typingTimer.current);
      if (streamTimer.current) clearInterval(streamTimer.current);
    };
  }, []);

  // Auto-scroll the thread to the newest message whenever it grows or when
  // the typing indicator appears / disappears. Uses the inner scroll box so
  // the whole landing page doesn't jump.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const streamAgentReply = useCallback((fullText, id) => {
    const words = fullText.split(" ");
    let idx = 0;
    // Insert a placeholder agent message that will accumulate words.
    setMessages((prev) => [
      ...prev,
      { id, role: "agent", kind: "streamed", text: "", streaming: true },
    ]);
    streamTimer.current = setInterval(() => {
      idx += 1;
      const partial = words.slice(0, idx).join(" ");
      const done = idx >= words.length;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id
            ? { ...m, text: partial, streaming: !done }
            : m,
        ),
      );
      if (done) {
        clearInterval(streamTimer.current);
        streamTimer.current = null;
        setReplyDelivered(true);
      }
    }, STREAM_WORD_INTERVAL_MS);
  }, []);

  const ask = useCallback(
    (rawText) => {
      const text = (rawText || "").trim();
      if (!text) return;
      // If a stream is currently in flight, cancel it before starting a new
      // turn — feels snappier than queuing up back-to-back demo responses.
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
        typingTimer.current = null;
      }
      if (streamTimer.current) {
        clearInterval(streamTimer.current);
        streamTimer.current = null;
        setMessages((prev) =>
          prev.map((m) =>
            m.streaming ? { ...m, streaming: false } : m,
          ),
        );
      }

      const userId = `u-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: userId, role: "user", text },
      ]);
      setTyping(true);

      typingTimer.current = setTimeout(() => {
        setTyping(false);
        const reply = classifyResponse(text);
        streamAgentReply(reply.text, `a-${Date.now()}`);
      }, TYPING_DELAY_MS);
    },
    [streamAgentReply],
  );

  useImperativeHandle(ref, () => ({ ask }), [ask]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    // Close the mobile keyboard so the streamed reply is visible.
    inputRef.current?.blur();
    ask(text);
  };

  return (
    <div className="mx-auto w-full max-w-[500px]" data-testid="chat-preview-wrapper">
      <div
        className="overflow-hidden"
        style={{
          background: "#EAF3DE",
          border: "0.5px solid #C0DD97",
          borderRadius: 14,
        }}
        data-testid="chat-preview"
      >
        {/* Header */}
        <div
          className="flex items-center gap-2.5 px-4 py-3"
          style={{ background: "#F4FAF2", borderBottom: "1px solid #D4EAD0" }}
          data-testid="chat-preview-header"
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-extrabold text-sm shrink-0"
            style={{ background: "#FF6B00" }}
            aria-hidden="true"
          >
            C
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-[13px] text-[#111]">CartIQ 360</div>
            <div className="text-[10.5px] font-semibold text-[#3B6D11] flex items-center gap-1">
              <span
                className="w-1.5 h-1.5 rounded-full inline-block"
                style={{ background: "#3B6D11" }}
                aria-hidden="true"
              />
              online
            </div>
          </div>
        </div>

        {/* Thread — scroll box */}
        <div
          ref={scrollRef}
          className="px-4 py-4 space-y-3 overflow-y-auto"
          style={{ maxHeight: 380, minHeight: 240 }}
          data-testid="chat-preview-messages"
          aria-live="polite"
        >
          {messages.map((m) => (
            <MessageBubble key={m.id} msg={m} />
          ))}
          {typing && <TypingBubble />}
        </div>

        {/* Input bar */}
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2 px-3 py-3"
          style={{ background: "#FFFFFF", borderTop: "1px solid #D4EAD0" }}
          data-testid="chat-preview-input"
        >
          <button
            type="button"
            tabIndex={-1}
            aria-label="Attach"
            className="p-1.5 text-[#94A3B8] hover:text-[#639922]"
            data-testid="chat-preview-attach"
            onClick={() => ask("Drop a Blinkit invoice")}
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <div
            className="flex-1 flex items-center rounded-full px-3 py-1.5"
            style={{ background: "#EAF3DE", border: "1px solid #C0DD97" }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask CartIQ anything about your food…"
              className="flex-1 bg-transparent outline-none text-[12.5px] text-[#111] placeholder:text-[#639922] py-1"
              data-testid="chat-preview-textinput"
              aria-label="Ask CartIQ a question"
              autoComplete="off"
              maxLength={200}
            />
          </div>
          <button
            type="submit"
            aria-label="Send"
            disabled={!input.trim()}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 hover:opacity-90 transition-opacity disabled:opacity-40"
            style={{ background: "#FF6B00" }}
            data-testid="chat-preview-send"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Post-reply CTA — appears once the first live reply has landed. Keeps
          the "aha" moment separate from the initial preview so the CTA feels
          earned, not spammy. */}
      {replyDelivered && (
        <div className="mt-4 flex justify-center" data-testid="chat-preview-cta-wrap">
          <Link
            to="/upload"
            className="inline-flex items-center gap-2 text-sm md:text-base px-6 py-3 font-bold text-white rounded-sm uppercase tracking-[0.08em] transition-colors shadow-sm hover:opacity-90"
            style={{ background: "#FF6B00" }}
            data-testid="chat-preview-cta"
          >
            See your real intelligence <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
});

export default ChatPreview;

// ─────────────────────────── sub-components ───────────────────────────

function MessageBubble({ msg }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid={`chat-msg-user-${msg.id}`}>
        <div
          className="max-w-[80%] rounded-2xl rounded-br-md px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-white font-medium break-words"
          style={{ background: "#FF6B00" }}
        >
          {msg.text}
        </div>
      </div>
    );
  }

  // Agent bubble — the seed "opener" also carries the 3 stat chips beneath.
  const isOpener = msg.kind === "opener";
  return (
    <div className="flex justify-start" data-testid={`chat-msg-agent-${msg.id}`}>
      <div
        className="max-w-[92%] rounded-2xl rounded-bl-md px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-[#111] break-words"
        style={{ background: "#FFFFFF", border: "1px solid #C0DD97" }}
      >
        <AgentText text={msg.text} streaming={!!msg.streaming} kind={msg.kind} />

        {isOpener && (
          <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="chat-msg-stat-chips">
            <StatChip label="Monthly spend" value="₹18,400" tone="#FF6B00" />
            <StatChip label="Nutrition score" value="54" tone="#3B6D11" />
            <StatChip label="To analyse" value="60s" tone="#0C447C" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders the agent text with a couple of niceties:
 *   • a blinking caret while the message is streaming
 *   • the trailing "→" replaced with a real <Link> that navigates to /upload
 *     (only for the "streamed" agent kind — the baked seed messages keep
 *     their plain text for a stable initial preview)
 */
function AgentText({ text, streaming, kind }) {
  const shouldLinkifyArrow = kind === "streamed" && !streaming;
  if (shouldLinkifyArrow && text.trim().endsWith("→")) {
    const withoutArrow = text.replace(/→\s*$/, "").trimEnd();
    return (
      <>
        {styleInline(withoutArrow)}{" "}
        <Link
          to="/upload"
          className="font-bold hover:underline"
          style={{ color: "#FF6B00" }}
          data-testid="chat-msg-arrow-link"
          aria-label="Go to upload"
        >
          →
        </Link>
      </>
    );
  }
  return (
    <>
      {styleInline(text)}
      {streaming && (
        <span
          className="inline-block w-[6px] h-[13px] ml-0.5 align-[-2px]"
          style={{ background: "#111", animation: "cartiq-caret 1s steps(1) infinite" }}
          aria-hidden="true"
        />
      )}
    </>
  );
}

// Lightly emphasise numbers + percentages inside the streamed text.
function styleInline(text) {
  const parts = text.split(/(₹[\d,]+|\d{1,3}%|\bscore is \d+|\d+\s?\/\s?\d+)/g);
  return parts.map((p, i) => {
    if (/₹[\d,]+/.test(p))
      return (
        <span key={i} className="font-bold" style={{ color: "#FF6B00" }}>
          {p}
        </span>
      );
    if (/^\d{1,3}%$/.test(p))
      return (
        <span key={i} className="font-bold" style={{ color: "#3B6D11" }}>
          {p}
        </span>
      );
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function TypingBubble() {
  return (
    <div className="flex justify-start" data-testid="chat-typing-indicator">
      <div
        className="rounded-2xl rounded-bl-md px-3.5 py-2.5"
        style={{ background: "#FFFFFF", border: "1px solid #C0DD97" }}
        aria-label="CartIQ is typing"
      >
        <div className="flex items-center gap-1">
          <span className="cartiq-dot" style={{ animationDelay: "0ms" }} />
          <span className="cartiq-dot" style={{ animationDelay: "160ms" }} />
          <span className="cartiq-dot" style={{ animationDelay: "320ms" }} />
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, tone }) {
  return (
    <div
      className="inline-flex items-baseline gap-1.5 px-2 py-1 rounded-md"
      style={{ background: "#FFFFFF", border: "1px solid #D4EAD0" }}
    >
      <span className="text-[11.5px] font-extrabold" style={{ color: tone }}>
        {value}
      </span>
      <span className="text-[9.5px] uppercase tracking-[0.08em] text-[#639922] font-semibold">
        {label}
      </span>
    </div>
  );
}
